import { formatDuration } from "../utils/duration";
import { getElementOfType } from "../utils/dom";
import platform from "../utils/platform";
const CSS_TRANSFORM_PROPERTY = "transform" in document.head.style ? "transform" : "webkitTransform";
const MS_PER_PIXEL_ANIM_CUTOFF = 500;
const TWENTY_FOUR_HRS_IN_SECONDS = 86400;
const LIVE_SEARCH_OFFSET = 55;
function ScrubberRenderMixin(Base) {
  return class extends Base {
    static get reactiveAttributes() {
      return {
        ...super.reactiveAttributes,
        active: Boolean
      };
    }
    static get reactiveProperties() {
      return {
        ...super.reactiveProperties,
        endTime: Number,
        isDragging: Boolean,
        isLive: Boolean,
        isRealtime: Boolean,
        isSeeking: Boolean,
        isTimeRunning: Boolean,
        playbackRate: Number,
        playheadPosition: Number,
        pointerPosition: Number,
        seekableStep: Number,
        startTime: Number,
        width: Number
      };
    }
    active = false;
    endTime = 0;
    isDragging = false;
    isLive = false;
    isRealtime = false;
    isSeeking = false;
    isTimeRunning = false;
    playbackRate = 1;
    playheadPosition = 0;
    pointerPosition;
    seekableStep = 1;
    startTime = 0;
    width = 0;
    epochStartTime = 0;
    player;
    _unshadowProperties;
    _elPlayheadTime = null;
    _elPlayheadTimeComputedStyles;
    _elEndTime = null;
    _elInputRange = null;
    _elPlayhead = null;
    _elPlayheadComputedStyles;
    _elProgressBarPlayed = null;
    _elProgressBarPlayedComputedStyles;
    _elPointerTime = null;
    _elStartTime = null;
    _elIndexPoints = null;
    _elScrubberImage = null;
    readyCallback() {
      this.player = getElementOfType(this);
      this.selectElements();
      this.width = this.offsetWidth;
      this._unshadowProperties = this.player.shadowProperties(
        ["isLive", "isRealtime", "isSeeking", "isTimeRunning", "playbackRate", "seekableStep"],
        this
      );
    }
    updating(propName, oldValue) {
      if (this.width !== this.offsetWidth) {
        this.width = this.offsetWidth;
      }
      if (propName === "active") {
        if (this.active && !this.width) {
          this.width = this.offsetWidth;
        }
      } else if (propName === "isDragging") {
        if (!this.isDragging && this.pointerPosition !== void 0) {
          this.isTimeRunning = false;
          this.playheadPosition = this.pointerPosition;
        }
      }
      super.updating(propName, oldValue);
    }
    updated(changedProperties) {
      super.updated(changedProperties);
      if (!this.endTime) {
        return;
      }
      const duration = this.endTime - this.startTime;
      if (changedProperties.has("pointerPosition")) {
        this.updatePointerTimeElement(duration);
      }
      if (changedProperties.has("active") || changedProperties.has("endTime") || changedProperties.has("startTime") || changedProperties.has("isTimeRunning") || changedProperties.has("playheadPosition") || changedProperties.has("playbackRate") || changedProperties.has("seekableStep") || changedProperties.has("width")) {
        const playheadTime = duration * this.playheadPosition;
        const playheadDisplayTime = this.isRealtime ? (this.epochStartTime + playheadTime) % TWENTY_FOUR_HRS_IN_SECONDS : playheadTime % TWENTY_FOUR_HRS_IN_SECONDS;
        const formattedPlayheadDisplayTime = formatDuration(playheadDisplayTime);
        const formattedDuration = formatDuration(duration);
        this.updateInputRangeElement(formattedPlayheadDisplayTime, formattedDuration, playheadTime);
        if (!this.active) {
          return;
        }
        const playheadX = Math.min(1, this.playheadPosition) * this.width;
        const msPerPixel = this.width ? duration * 1e3 * this.playbackRate / this.width : 0;
        const shouldAnimate = !this.isLive && (msPerPixel > 0 ? !platform.isIE && msPerPixel <= MS_PER_PIXEL_ANIM_CUTOFF : false);
        const animationDuration = msPerPixel * (this.width - playheadX);
        if (!this.isDragging) {
          this.updateProgressBarPlayedElement();
        }
        this.updateIndexPointsElement();
        this.updatePlayheadElement(shouldAnimate, animationDuration, playheadX);
        const [elPlayheadTimeLeftEdge, elPlayheadTimeRightEdge] = this.updatePlayheadTimeElement(
          formattedPlayheadDisplayTime,
          playheadX,
          shouldAnimate,
          animationDuration,
          playheadTime
        );
        this.updateStartTimeElement(elPlayheadTimeLeftEdge);
        this.updateEndTimeElement(playheadTime, elPlayheadTimeRightEdge);
      }
    }
    updateIndexPointsElement() {
      if (this._elIndexPoints) {
        this._elIndexPoints.setAttribute("endtime", this.endTime.toString());
        this._elIndexPoints.setAttribute(
          "currenttime",
          ((this.endTime - this.startTime) * this.playheadPosition).toString()
        );
      }
    }
    updateScrubberImageElement(pointerX, maxPointerX, pointerTime) {
    }
    updatePointerTimeElement(duration) {
      if (this.pointerPosition !== void 0) {
        const pointerX = this.pointerPosition * this.width;
        const pointerTime = duration * this.pointerPosition;
        if (this._elPointerTime) {
          const pointerDisplayTime = this.isRealtime ? (this.epochStartTime + pointerTime) % TWENTY_FOUR_HRS_IN_SECONDS : pointerTime % TWENTY_FOUR_HRS_IN_SECONDS;
          this._elPointerTime.innerText = formatDuration(pointerDisplayTime);
          const width = this._elPointerTime.offsetWidth;
          const widthHalf = width / 2;
          const maxPointerX = this.width - width;
          this._elPointerTime.style.visibility = "visible";
          this._elPointerTime.style[CSS_TRANSFORM_PROPERTY] = `translateX(${Math.max(
            0,
            Math.min(pointerX - widthHalf, maxPointerX)
          )}px)`;
          this.updateScrubberImageElement(pointerX, maxPointerX, pointerDisplayTime);
        }
        if (this.isDragging) {
          if (this._elPlayhead) {
            this._elPlayhead.style[CSS_TRANSFORM_PROPERTY] = `translateX(${pointerX}px)`;
            this._elPlayhead.style.transitionDuration = "unset";
          }
          if (this._elInputRange) {
            this._elInputRange.value = `${pointerTime}`;
          }
          if (this._elProgressBarPlayed) {
            const playheadPosition = Math.min(1, this.pointerPosition);
            this._elProgressBarPlayed.style.width = `100%`;
            this._elProgressBarPlayed.style.transformOrigin = `center left`;
            this._elProgressBarPlayed.style.transform = `scaleX(${playheadPosition})`;
          }
        }
      } else if (this._elPointerTime) {
        this._elPointerTime.style.visibility = "hidden";
        if (this._elScrubberImage) {
          this._elScrubberImage.style.visibility = "hidden";
        }
      }
    }
    updateInputRangeElement(formattedPlayheadDisplayTime, formattedDuration, playheadTime) {
      if (this._elInputRange) {
        this._elInputRange.setAttribute("step", `${this.seekableStep}`);
        this._elInputRange.setAttribute("min", `${this.startTime}`);
        this._elInputRange.setAttribute("max", `${this.endTime}`);
        this._elInputRange.setAttribute(
          "aria-valuetext",
          this.isRealtime ? formattedPlayheadDisplayTime : `${formattedPlayheadDisplayTime} av ${formattedDuration}`
        );
        if (!this.isDragging) {
          this._elInputRange.value = `${playheadTime}`;
        }
      }
    }
    updateProgressBarPlayedElement() {
      const playheadPosition = Math.min(1, this.playheadPosition);
      if (this._elProgressBarPlayed) {
        this._elProgressBarPlayed.style.width = `100%`;
        this._elProgressBarPlayed.style.transformOrigin = `center left`;
        this._elProgressBarPlayed.style.transform = `scaleX(${playheadPosition})`;
        this._elProgressBarPlayed.style.transitionDuration = "unset";
      }
    }
    updatePlayheadElement(shouldAnimate, animationDuration, playheadX) {
      if (!this.isDragging && this._elPlayhead) {
        this._elPlayhead.style[CSS_TRANSFORM_PROPERTY] = `translateX(${playheadX}px)`;
        this._elPlayhead.style.transitionDuration = "unset";
        this._elPlayhead.style.willChange = "transform";
        if (this.isTimeRunning && shouldAnimate) {
          this._elPlayheadComputedStyles[CSS_TRANSFORM_PROPERTY];
          this._elPlayhead.style[CSS_TRANSFORM_PROPERTY] = `translateX(${this.width}px)`;
          this._elPlayhead.style.transitionDuration = `${animationDuration}ms`;
        }
      }
    }
    updatePlayheadTimeElement(formattedPlayheadDisplayTime, playheadX, shouldAnimate, animationDuration, playheadTime) {
      let elPlayheadTimeLeftEdge = 0;
      let elPlayheadTimeRightEdge = 0;
      if (this._elPlayheadTime) {
        const width = this._elPlayheadTime.offsetWidth;
        const widthHalf = Math.round(width / 2);
        const boundsLeft = widthHalf;
        const boundsRight = this.width - (this._elPointerTime && this._elPointerTime?.offsetWidth / 2 > widthHalf ? this._elPointerTime?.offsetWidth / 2 : widthHalf);
        const xTarget = Math.min(Math.max(playheadX, boundsLeft), boundsRight);
        const isSticky = playheadX < widthHalf || playheadX > boundsRight;
        elPlayheadTimeLeftEdge = xTarget - widthHalf - 2;
        elPlayheadTimeRightEdge = xTarget + widthHalf + 2;
        this._elPlayheadTime.innerText = formattedPlayheadDisplayTime;
        this._elPlayheadTime.style.marginLeft = `-${widthHalf}px`;
        this._elPlayheadTime.style[CSS_TRANSFORM_PROPERTY] = `translateX(${xTarget}px)`;
        this._elPlayheadTime.style.transitionDuration = `unset`;
        if (!isSticky && this.isTimeRunning && shouldAnimate) {
          this._elPlayheadTimeComputedStyles[CSS_TRANSFORM_PROPERTY];
          this._elPlayheadTime.style[CSS_TRANSFORM_PROPERTY] = `translateX(${this.width}px)`;
          this._elPlayheadTime.style.transitionDuration = `${animationDuration}ms`;
        }
      }
      return [elPlayheadTimeLeftEdge, elPlayheadTimeRightEdge];
    }
    updateStartTimeElement(elPlayheadTimeLeftEdge) {
      if (this._elStartTime) {
        if (this.isRealtime) {
          const displayTime = this.isRealtime ? this.epochStartTime % TWENTY_FOUR_HRS_IN_SECONDS : this.startTime;
          this._elStartTime.innerText = `${formatDuration(displayTime)}`;
          this._elStartTime.style.visibility = elPlayheadTimeLeftEdge > this._elStartTime.offsetWidth ? "visible" : "hidden";
        } else {
          this._elStartTime.style.visibility = "hidden";
        }
      }
    }
    updateEndTimeElement(playheadTime, elPlayheadTimeRightEdge) {
      const duration = this.endTime - this.startTime;
      if (this._elEndTime) {
        const displayTime = this.isRealtime ? (this.epochStartTime + duration) % TWENTY_FOUR_HRS_IN_SECONDS : this.endTime;
        const formattedDisplayTime = formatDuration(this.isRealtime ? displayTime : playheadTime - this.endTime);
        this._elEndTime.innerText = `${this.isRealtime ? "" : "-"}${formattedDisplayTime}`;
        this._elEndTime.style.visibility = elPlayheadTimeRightEdge < this.width - this._elEndTime.offsetWidth ? "visible" : "hidden";
      }
    }
    selectElements() {
      if (this._elPlayheadTime) {
        this._elPlayheadTimeComputedStyles = getComputedStyle(this._elPlayheadTime);
      }
      if (this._elPlayhead) {
        this._elPlayheadComputedStyles = getComputedStyle(this._elPlayhead);
      }
      if (this._elProgressBarPlayed) {
        this._elProgressBarPlayedComputedStyles = getComputedStyle(this._elProgressBarPlayed);
      }
    }
    disconnectedCallback() {
      this._unshadowProperties?.();
      this.player = null;
      super.disconnectedCallback && super.disconnectedCallback();
    }
  };
}
export {
  LIVE_SEARCH_OFFSET,
  ScrubberRenderMixin,
  TWENTY_FOUR_HRS_IN_SECONDS
};
